import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { formatKRW } from "@shared/pricing";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";

type Editable = { price: string; name: string };

export default function AdminProductsPage() {
  const list = trpc.products.listAll.useQuery();
  const utils = trpc.useUtils();
  const [edits, setEdits] = useState<Record<number, Editable>>({});

  useEffect(() => {
    if (!list.data) return;
    const initial: Record<number, Editable> = {};
    list.data.forEach((p) => {
      initial[p.id] = { price: String(p.price), name: p.name };
    });
    setEdits(initial);
  }, [list.data]);

  const update = trpc.products.updatePrice.useMutation({
    onSuccess: () => {
      toast.success("상품 정보를 업데이트했습니다.");
      utils.products.listAll.invalidate();
      utils.products.listActive.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const grouped = (list.data ?? []).reduce<
    Record<string, NonNullable<typeof list.data>>
  >((acc, p) => {
    (acc[p.kind] ??= []).push(p);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <h1 className="font-serif text-2xl text-primary mb-6">상품 가격 관리</h1>
      {list.isLoading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([kind, items]) => (
            <Card key={kind}>
              <CardContent className="p-5">
                <div className="font-serif text-lg text-primary mb-3 capitalize">
                  {kind === "size"
                    ? "캔버스 사이즈"
                    : kind === "frame"
                      ? "프레임"
                      : kind === "finish"
                        ? "유화 질감"
                        : kind}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left p-2">코드</th>
                        <th className="text-left p-2">이름</th>
                        <th className="text-right p-2">현재가</th>
                        <th className="text-right p-2">새 가격</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p) => {
                        const e = edits[p.id] ?? {
                          price: String(p.price),
                          name: p.name,
                        };
                        return (
                          <tr
                            key={p.id}
                            className="border-t border-border/60"
                          >
                            <td className="p-2 font-mono text-xs">
                              {p.code}
                            </td>
                            <td className="p-2">
                              <Input
                                value={e.name}
                                onChange={(v) =>
                                  setEdits({
                                    ...edits,
                                    [p.id]: {
                                      ...e,
                                      name: v.target.value,
                                    },
                                  })
                                }
                                className="h-8"
                              />
                            </td>
                            <td className="p-2 text-right text-muted-foreground">
                              {formatKRW(p.price)}
                            </td>
                            <td className="p-2 text-right">
                              <Input
                                type="number"
                                value={e.price}
                                onChange={(v) =>
                                  setEdits({
                                    ...edits,
                                    [p.id]: {
                                      ...e,
                                      price: v.target.value,
                                    },
                                  })
                                }
                                className="h-8 text-right max-w-32 ml-auto"
                              />
                            </td>
                            <td className="p-2 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={update.isPending}
                                onClick={() => {
                                  const priceNum = Number(e.price);
                                  if (Number.isNaN(priceNum) || priceNum < 0) {
                                    toast.error("가격을 확인해주세요.");
                                    return;
                                  }
                                  update.mutate({
                                    id: p.id,
                                    price: priceNum,
                                    name: e.name,
                                  });
                                }}
                              >
                                저장
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
